/**
 * Which cycle this page is working in, said to the server on every write.
 *
 * An item's lock is relative to a cycle (migration 1019, roadmap S-37). The item
 * page sets this while it shows a cycle still ranking the item; every write it
 * makes then carries the `x-item-cycle` header, so a change is checked against
 * that cycle and recorded on the item version it makes.
 *
 * It is a statement, not a permission. The server checks it on every write: the
 * cycle must still be ranking and the item must be in it. With no cycle set,
 * nothing is sent and the plan in force holds the item for everyone.
 *
 * `ITEM_PLAN_AMENDMENT_HEADER` names a plan the person may amend; the server
 * checks that too (internal_item_amendment_in_context). The item page sets it
 * while a person who may amend the plan has a held field open to change, and the
 * plan keeps that change out until it is authorized again.
 */

import { ITEM_CYCLE_HEADER } from '../../supabase/functions/_shared/itemCycleHeader.ts';

export const ITEM_PLAN_AMENDMENT_HEADER = 'x-item-plan-amendment';

let workingCycleId: string | null = null;
let amendingPlanId: string | null = null;

export function setWorkingCycle(cycleId: string | null): void {
  workingCycleId = cycleId;
}

/** The plan a person is amending from the item page; null when none. */
export function setAmendingPlan(planCycleId: string | null): void {
  amendingPlanId = planCycleId;
}

/** Headers with the working cycle, or the plan being amended, added, for a write through the database API. */
export function withWorkingCycle(url: string, init: RequestInit): RequestInit {
  if (!workingCycleId && !amendingPlanId) return init;
  const method = (init.method ?? 'GET').toUpperCase();
  if (method === 'GET' || method === 'HEAD' || !url.includes('/rest/v1/')) return init;
  const headers = new Headers(init.headers);
  if (workingCycleId) headers.set(ITEM_CYCLE_HEADER, workingCycleId);
  if (amendingPlanId) headers.set(ITEM_PLAN_AMENDMENT_HEADER, amendingPlanId);
  return { ...init, headers };
}
